import React, { useEffect, useRef, useState } from "react";
import { hasUnsavedChanges } from "../../utils/logoutGuard";

// Tells the user when a newer version of the app has been deployed, so they can
// pick it up with one click instead of logging out / hard refreshing / clearing
// cookies.
//
// Why it's needed: the browser (and IIS, which sends no Cache-Control by
// default) can keep serving a cached index.html, which points at the OLD
// hashed JS bundle - so a user can keep running last week's version for as long
// as the tab stays open. Here index.html is re-fetched with `no-store` (and a
// cache-busting query) on an interval and whenever the tab regains focus, and
// the hashed /static/ asset names it references are compared with the ones this
// page actually loaded. Any difference = a new deployment.
//
// Nothing runs in `npm start` (dev serves unhashed bundles, so there is nothing
// to compare) - it only activates on a real production build.

const CHECK_INTERVAL_MS = 2 * 60 * 1000;
const MIN_GAP_BETWEEN_CHECKS_MS = 30 * 1000;

const isStaticAsset = (url) => /\/static\/(js|css)\//.test(url || "");

const signatureOf = (doc) => {
  const urls = [];
  doc.querySelectorAll("script[src]").forEach((el) => {
    const src = el.getAttribute("src");
    if (isStaticAsset(src)) urls.push(src.replace(/^https?:\/\/[^/]+/, ""));
  });
  doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((el) => {
    const href = el.getAttribute("href");
    if (isStaticAsset(href)) urls.push(href.replace(/^https?:\/\/[^/]+/, ""));
  });
  return urls.sort().join("|");
};

const AppUpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const loadedSignatureRef = useRef(signatureOf(document));
  const lastCheckRef = useRef(0);
  const checkingRef = useRef(false);

  useEffect(() => {
    // No hashed assets = dev server (or an unusual setup) - nothing to compare.
    if (!loadedSignatureRef.current) return undefined;

    let cancelled = false;

    const checkForUpdate = async () => {
      if (checkingRef.current) return;
      if (Date.now() - lastCheckRef.current < MIN_GAP_BETWEEN_CHECKS_MS) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      checkingRef.current = true;
      lastCheckRef.current = Date.now();
      try {
        const base = process.env.PUBLIC_URL || "";
        const response = await fetch(`${base}/index.html?_=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!response.ok) return;
        const html = await response.text();
        const latest = signatureOf(new DOMParser().parseFromString(html, "text/html"));
        // An empty signature means we got something that isn't the app's
        // index.html (an error page, a login redirect, ...) - never treat that
        // as "new version".
        if (!cancelled && latest && latest !== loadedSignatureRef.current) {
          setUpdateAvailable(true);
        }
      } catch (error) {
        // Offline / server restarting - just try again next time.
      } finally {
        checkingRef.current = false;
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };

    const initialTimer = setTimeout(checkForUpdate, 15000);
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  if (!updateAvailable || dismissed) return null;

  const updateNow = () => {
    if (
      hasUnsavedChanges() &&
      !window.confirm("You have unsaved changes on this page that will be lost. Update anyway?")
    ) {
      return;
    }
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 20,
        transform: "translateX(-50%)",
        zIndex: 2000,
        maxWidth: "calc(100vw - 24px)",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        padding: "12px 16px",
        borderRadius: 12,
        backgroundColor: "#fff",
        border: "1px solid #fed7aa",
        borderLeft: "4px solid #ef7f1a",
        boxShadow: "0 8px 28px rgba(15, 23, 42, 0.18)",
        fontFamily: "inherit",
      }}
    >
      <div>
        <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1f2d3d" }}>
          A new version is available
        </div>
        <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
          Update to get the latest changes and fixes.
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          style={{
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "#64748b",
            borderRadius: 8,
            padding: "7px 12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Later
        </button>
        <button
          type="button"
          onClick={updateNow}
          style={{
            border: "none",
            background: "#ef7f1a",
            color: "#fff",
            borderRadius: 8,
            padding: "7px 14px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Update now
        </button>
      </div>
    </div>
  );
};

export default AppUpdateBanner;
